class HeaderManager {
  constructor() {
    this.header = document.querySelector('.site-header');
    this.stickyHeader = document.querySelector('.header-sticky');
    this.menuTrigger = this.header?.dataset.menuTrigger || 'hover';
    this.isVoiceSearchSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

    const initDelay = window.requestIdleCallback ? requestIdleCallback : (cb) => setTimeout(cb, 200);
    initDelay(() => this.init());
  }

  init() {
    this.initMenuFocusability();
    this.setupMenuInteractions();
    this.setupSearchFunctionality();
    this.setupDrawerMenu();
    this.setupMobileMenu();
    this.setupVoiceSearch();
    this.setupCartUpdates();
    this.setupStickyHeader();
    this.setupScrollCar();
    this.cachedHeaderHeight = 0;
    this.cachedMenuTop = 0;

    this.updateMenuTop();
    window.addEventListener('resize', Kinderly.debounce(() => {
      this.updateMenuTop();
    }, 200));
  }

  updateMenuTop() {
    const header = this.header;
    if (!header) return;

    requestAnimationFrame(() => {
      const rect = header.getBoundingClientRect();
      const height = rect.height;
      const menuTop = rect.bottom;

      if (this.cachedMenuTop !== menuTop || this.cachedHeaderHeight !== height) {
        this.cachedMenuTop = menuTop;
        this.cachedHeaderHeight = height;
        document.documentElement.style.setProperty('--menu-top', menuTop + 'px');
        document.documentElement.style.setProperty('--header-height', height + 'px');
      }
    });
  }


  setupScrollCar() {
    const fill = document.getElementById('scroll-progress-fill');
    if (!fill) return;

    const car = fill.querySelector('.scroll-car-icon');
    let lastScrollY = window.scrollY;

    let cachedScrollTotal = 0;
    const getScrollTotal = () => {
      if (cachedScrollTotal > 0) return cachedScrollTotal;
      cachedScrollTotal = document.documentElement.scrollHeight - window.innerHeight;
      return cachedScrollTotal;
    };

    window.addEventListener('resize', () => { cachedScrollTotal = 0; });

    const svg = car ? car.querySelector('svg') : null;
    const isDiscount = svg && svg.classList.contains('icon-discount');
    const isSymmetrical = svg && (svg.classList.contains('icon-button-star') || svg.classList.contains('icon-heading-icon'));
    const defaultScaleX = isDiscount ? -1 : 1;

    if (car) {
      if (isSymmetrical) {
        car.style.transform = 'translate(-50%, -50%)';
      } else {
        car.style.transform = `translate(-50%, -50%) scaleX(${defaultScaleX})`;
      }
    }

    const updateProgress = (scrollY) => {
      const scrollTotal = getScrollTotal();
      if (scrollTotal <= 0) return;

      const scrollProgress = Math.min(100, Math.max(0, (scrollY / scrollTotal) * 100));
      fill.style.setProperty('--scroll-progress', scrollProgress + '%');
      fill.style.width = scrollProgress + '%';

      if (car) {
        if (isSymmetrical) {
          car.style.transform = 'translate(-50%, -50%)';
        } else {
          if (scrollY > lastScrollY + 5) {
            car.style.transform = `translate(-50%, -50%) scaleX(${defaultScaleX})`;
          } else if (scrollY < lastScrollY - 5) {
            car.style.transform = `translate(-50%, -50%) scaleX(${-defaultScaleX})`;
          }
        }
      }
      lastScrollY = scrollY;
    };

    updateProgress(window.scrollY);

    if (window.lenis) {
      window.lenis.on('scroll', (e) => updateProgress(e.animatedScroll));
    } else {
      window.addEventListener('scroll', () => updateProgress(window.scrollY), { passive: true });
    }
  }

  initMenuFocusability() {
    const focusableSelectors = 'a, button, input, select, textarea';
    document.querySelectorAll('.menu-item.has-dropdown, .menu-item.has-mega-menu').forEach(item => {
      item.querySelectorAll(`.dropdown-menu ${focusableSelectors}, .mega-menu ${focusableSelectors}`).forEach(el => {
        el.setAttribute('tabindex', '-1');
      });
    });
  }

  setupMenuInteractions() {
    const menuItems = document.querySelectorAll('.menu-item');

    menuItems.forEach(item => {
      const link = item.querySelector('.menu-link');
      const hasSubmenu = item.classList.contains('has-mega-menu') ||
        item.classList.contains('has-dropdown');

      if (this.menuTrigger === 'click') {
        if (!hasSubmenu) return;
        link.addEventListener('click', (e) => {
          const isExpanded = link.getAttribute('aria-expanded') === 'true';

          e.preventDefault();
          e.stopPropagation();

          if (!isExpanded) {
            this.closeAllMenuItems();
            this.openMenuItem(item);
          } else {
            this.closeMenuItem(item);
          }
        });
      } else {
        item.addEventListener('mouseenter', () => {
          clearTimeout(item._hoverTimeout);
          if (hasSubmenu) {
            item._openTimeout = setTimeout(() => {
              const parent = item.parentElement;
              if (parent) {
                Array.from(parent.children).forEach(sibling => {
                  if (sibling !== item && sibling.classList.contains('menu-active')) {
                    this.closeMenuItem(sibling);
                    clearTimeout(sibling._hoverTimeout);
                    clearTimeout(sibling._openTimeout);
                  }
                });
              }
              this.openMenuItem(item);
            }, 150);
          } else {
            item._openTimeout = setTimeout(() => {
              this.closeAllMenuItems();
            }, 150);
          }
        });

        item.addEventListener('mouseleave', () => {
          clearTimeout(item._openTimeout);
          if (hasSubmenu) {
            item._hoverTimeout = setTimeout(() => {
              this.closeMenuItem(item);
            }, 400);
          }
        });

        if (hasSubmenu) {
          item.addEventListener('focusin', () => {
            clearTimeout(item._hoverTimeout);
            clearTimeout(item._openTimeout);
            this.openMenuItem(item);
          });

          item.addEventListener('focusout', (e) => {
            if (!item.contains(e.relatedTarget)) {
              this.closeMenuItem(item);
            }
          });
        }
      }
    });

    const mainMenu = document.querySelector('.main-menu');
    if (mainMenu && this.menuTrigger !== 'click') {
      mainMenu.addEventListener('mouseleave', () => {
        this.closeAllMenuItems();
      });
    }

    if (this.menuTrigger === 'click') {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.menu-item')) {
          this.closeAllMenuItems();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllMenuItems();
        }
      });
    }

    const submenuItems = document.querySelectorAll('.submenu-item-link-list');
    submenuItems.forEach(item => {
      const link = item.querySelector('.dropdown-link');
      if (!link) return;

      if (this.menuTrigger === 'click') {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const isExpanded = item.classList.contains('submenu-active');

          if (!isExpanded) {
            const siblings = item.parentElement.querySelectorAll('.submenu-active');
            siblings.forEach(sibling => sibling.classList.remove('submenu-active'));
            item.classList.add('submenu-active');
          } else {
            item.classList.remove('submenu-active');
          }
        });
      }
    });
  }

  toggleMenuItem(item) {
    const link = item.querySelector('.menu-link');
    const isExpanded = link.getAttribute('aria-expanded') === 'true';

    this.closeAllMenuItems();

    if (!isExpanded) {
      this.openMenuItem(item);
    }
  }

  openMenuItem(item) {
    const link = item.querySelector('.menu-link');

    item.classList.add('menu-active');
    link.setAttribute('aria-expanded', 'true');
    item.setAttribute('aria-expanded', 'true');

    const focusableSelectors = 'a, button, input, select, textarea, [tabindex]';
    item.querySelectorAll(`.dropdown-menu ${focusableSelectors}, .mega-menu ${focusableSelectors}`).forEach(el => {
      el.removeAttribute('tabindex');
    });

    document.body.classList.add('menu-open');
  }

  closeMenuItem(item) {
    const link = item.querySelector('.menu-link');
    clearTimeout(item._openTimeout);
    clearTimeout(item._hoverTimeout);

    item.classList.remove('menu-active');
    link.setAttribute('aria-expanded', 'false');
    item.setAttribute('aria-expanded', 'false');

    const focusableSelectors = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    item.querySelectorAll(`.dropdown-menu ${focusableSelectors}, .mega-menu ${focusableSelectors}`).forEach(el => {
      el.setAttribute('tabindex', '-1');
    });

    document.body.classList.remove('menu-open');
  }

  closeAllMenuItems() {
    const menuItems = document.querySelectorAll('.menu-item');
    menuItems.forEach(item => this.closeMenuItem(item));
    document.body.classList.remove('menu-open');
  }

  setupSearchFunctionality() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchOverlay = document.getElementById('search-overlay');
    const searchClose = document.querySelector('.search-close');
    const searchInputs = document.querySelectorAll('.search-input');
    const searchForm = document.querySelector('.search-form');

    if (!searchToggle || !searchOverlay) return;

    searchToggle.addEventListener('click', () => {
      this.openSearch();
    });

    searchClose?.addEventListener('click', () => {
      this.closeSearch();
    });

    searchOverlay.addEventListener('click', (e) => {
      if (e.target === searchOverlay) {
        this.closeSearch();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeSearch();
      }
    });

    searchForm?.addEventListener('submit', (e) => {
      const activeInput = document.querySelector('.search-input:focus') || searchInputs[0];
      const query = activeInput?.value.trim();
      if (!query) {
        e.preventDefault();
        return;
      }
    });

    searchInputs.forEach(searchInput => {
      let searchTimeout;

      searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        const query = e.target.value.trim();

        searchInputs.forEach(input => {
          if (input !== searchInput) {
            input.value = query;
          }
        });

        if (query.length > 0) {
          this.hideDefaultSuggestions();

          searchTimeout = setTimeout(() => {
            this.performLiveSearch(query);
          }, 300);
        } else {
          this.showDefaultSuggestions();
          this.clearLiveSearchResults();
        }
      });

      searchInput.addEventListener('focus', (e) => {
        const query = e.target.value.trim();
        if (!query) {
          this.showDefaultSuggestions();
        }
      });
    });
  }

  openSearch() {
    const searchOverlay = document.getElementById('search-overlay');
    const searchInputs = document.querySelectorAll('.search-input');

    this.lastFocusedElement = document.activeElement;
    searchOverlay.classList.add('active');
    document.body.classList.add('no-scroll');
    document.body.classList.add('active-search');

    this.showDefaultSuggestions();

    setTimeout(() => {
      searchInputs[0]?.focus();
      this.trapFocus(searchOverlay);
    }, 300);
  }

  closeSearch() {
    const searchOverlay = document.getElementById('search-overlay');
    const searchInputs = document.querySelectorAll('.search-input');

    searchOverlay.classList.remove('active');
    document.body.classList.remove('no-scroll');
    document.body.classList.remove('active-search');

    searchInputs.forEach(input => {
      input.value = '';
    });

    this.showDefaultSuggestions();
    this.clearLiveSearchResults();

    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
  }

  trapFocus(container) {
    const allFocusableElements = container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const focusableElements = Array.from(allFocusableElements).filter(el => {
      if (el.getAttribute('aria-hidden') === 'true' || el.closest('[aria-hidden="true"]')) return false;
      if (el.offsetParent === null && el.style.position !== 'fixed') return false;

      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });

    if (focusableElements.length === 0) return;

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    };

    container.removeEventListener('keydown', container._focusTrapListener);
    container._focusTrapListener = handleKeyDown;
    container.addEventListener('keydown', handleKeyDown);
  }

  showDefaultSuggestions() {
    const defaultSuggestions = document.getElementById('default-suggestions');
    const resultsContainer = document.getElementById('search-results');

    if (defaultSuggestions) {
      defaultSuggestions.style.display = 'block';
    }

    if (resultsContainer) {
      resultsContainer.classList.add('has-results');
    }
  }

  hideDefaultSuggestions() {
    const defaultSuggestions = document.getElementById('default-suggestions');
    if (defaultSuggestions) {
      defaultSuggestions.style.display = 'none';
    }
  }

  async performLiveSearch(query) {
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,page,collection&resources[limit]=10&resources[options][fields]=title,body,product_type,variants.title`);
      const data = await response.json();

      this.displaySearchResults(data);
    } catch (error) {
    }
  }

  displaySearchResults(data) {
    const liveResultsContainer = document.getElementById('live-search-results');
    const noResultsElement = document.getElementById('predictive-no-results');
    const noResultsMessage = document.getElementById('predictive-no-results-message');

    if (!liveResultsContainer) return;

    let html = '';
    let hasResults = false;
    let pendingCollections = [];

    if (data.resources && data.resources.results) {
      const { products = [], collections = [], articles = [], pages = [] } = data.resources.results;

      if (products.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchProducts || 'Products'}</h3><ul class="search-results-items">`;
        products.forEach(product => {
          html += `
          <li class="search-result-item">
            <a href="${product.url}" aria-label="${product.title}">
              <div class="search-result-item-image-wrap">
                <div class="search-result-item-image-wrap-inner">
                  ${(product.featured_image && product.featured_image.url) ? `<img src="${product.featured_image.url}" alt="${product.title}" width="100" height="100" loading="lazy">` : `<div class="predictive-search-placeholder">${this.getPlaceholderSvg('product-1')}</div>`}
                </div>
              </div>
              <div class="search-result-item-flex">
                <span class="object-type-badge">Product</span>
                <span>${product.title}</span>
                <div class="search-result-item-meta">
                  <span class="price">${this.formatMoney(product.price, window.themeVariables?.settings?.currencyCodeEnabled ? window.themeVariables?.settings?.moneyWithCurrencyFormat : window.themeVariables?.settings?.moneyFormat)}</span>
                </div>
              </div>
            </a>
          </li>
        `;
        });
        html += '</ul></div>';
      }

      if (collections.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchCollections || 'Collections'}</h3><ul class="search-results-items">`;
        collections.forEach(collection => {
          const hasImage = collection.featured_image && collection.featured_image.url;
          const imgId = `predictive-col-img-${collection.id}`;
          html += `
          <li class="search-result-item">
            <a href="${collection.url}" aria-label="${collection.title}">
              ${hasImage ? `<img src="${collection.featured_image.url}" alt="${collection.title}" width="100" height="100" loading="lazy">` : `<div id="${imgId}-wrapper" style="display:contents;"></div>`}
              <div class="search-result-item-flex">
                <span class="object-type-badge">Collection</span>
                <span>${collection.title}</span>
              </div>
            </a>
          </li>
        `;
          if (!hasImage) {
            pendingCollections.push({ handle: collection.handle, imgId: imgId });
          }
        });
        html += '</ul></div>';
      }

      if (articles.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchArticles || 'Articles'}</h3><ul class="search-results-items">`;
        articles.forEach(article => {
          html += `
          <li class="search-result-item">
            <a href="${article.url}" aria-label="${article.title}">
              ${article.image ? `<img src="${article.image}" alt="${article.title}" width="100" height="100" loading="lazy">` : `<div class="predictive-search-placeholder">${this.getPlaceholderSvg('article-1')}</div>`}
              <div class="search-result-item-flex">
                <span class="object-type-badge">Article</span>
                <span>${article.title}</span>
              </div>
            </a>
          </li>
        `;
        });
        html += '</ul></div>';
      }

      if (pages.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchPages || 'Pages'}</h3><ul class="search-results-items">`;
        pages.forEach(page => {
          html += `
          <li class="search-result-item">
            <a href="${page.url}" aria-label="${page.title}">
              <div class="search-result-item-flex">
                <span class="object-type-badge">Page</span>
                <span>${page.title}</span>
              </div>
            </a>
          </li>
        `;
        });
        html += '</ul></div>';
      }

      if (hasResults) {
        const searchInput = document.querySelector('.search-input');
        const query = searchInput ? searchInput.value.trim() : '';
        const searchUrl = window.shopRoutes?.searchUrl || '/search';
        html += `
        <div class="search-view-all">
          <a href="${searchUrl}?q=${encodeURIComponent(query)}" aria-label="${window.themeVariables?.strings?.searchViewAll || 'View all results'} ${query}" class="btn-view-all">
            ${window.themeVariables?.strings?.searchViewAll || 'View all results for'} "${query}"
          </a>
        </div>
      `;

        if (noResultsElement) {
          noResultsElement.style.display = 'none';
        }
      } else {
        if (noResultsElement) {
          const searchInput = document.querySelector('.search-input');
          const query = searchInput ? searchInput.value.trim() : '';

          if (noResultsMessage) {
            noResultsMessage.textContent = `${window.themeVariables?.strings?.searchNoResults || 'No results found for'} "${query}"`;
          }

          noResultsElement.style.display = 'block';
        }
      }
    }

    liveResultsContainer.innerHTML = html;

    const resultsContainer = document.getElementById('search-results');
    if (resultsContainer) {
      resultsContainer.classList.add('has-results');
    }

    if (pendingCollections.length > 0) {
      pendingCollections.forEach(item => {
        this.fetchCollectionFallbackImage(item.handle, item.imgId);
      });
    }
  }

  async fetchCollectionFallbackImage(handle, imgId) {
    try {
      const response = await fetch(`/collections/${handle}/products.json?limit=1`);
      if (response.ok) {
        const data = await response.json();
        if (data.products && data.products.length > 0) {
          const firstProduct = data.products[0];
          if (firstProduct.images && firstProduct.images.length > 0) {
            const wrapper = document.getElementById(`${imgId}-wrapper`);
            if (wrapper) {
              wrapper.innerHTML = `<img src="${firstProduct.images[0].src}" alt="${handle}" width="100" height="100" loading="lazy">`;
              return;
            }
          }
        }
      }
      this.showCollectionPlaceholder(imgId);
    } catch (e) {
      this.showCollectionPlaceholder(imgId);
    }
  }

  showCollectionPlaceholder(imgId) {
    const wrapper = document.getElementById(`${imgId}-wrapper`);
    if (wrapper) {
      wrapper.innerHTML = `<div class="predictive-search-placeholder">${this.getPlaceholderSvg('collection-1')}</div>`;
    }
  }

  clearLiveSearchResults() {
    const liveResultsContainer = document.getElementById('live-search-results');
    if (liveResultsContainer) {
      liveResultsContainer.innerHTML = '';
    }
  }

  formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    if (!format) {
      const vars = window.themeVariables?.settings;
      format = vars?.currencyCodeEnabled ? vars?.moneyWithCurrencyFormat : vars?.moneyFormat;
    }
    if (!format) format = '${{amount}}';

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const value = (cents / 100).toFixed(2);
    return format.replace(placeholderRegex, value);
  }

  getPlaceholderSvg(type) {
    const placeholders = {
      'product-1': '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" class="placeholder-svg"><path d="M15.5 5H11V2c0-.55-.45-1-1-1s-1 .45-1 1v3H4.5c-.83 0-1.5.67-1.5 1.5v11c0 .83.67 1.5 1.5 1.5h11c.83 0 1.5-.67 1.5-1.5v-11c0-.83-.67-1.5-1.5-1.5zm-4.5 0H9V3h2v2zm4.5 12.5h-11v-11h11v11zM7 9h6v2H7V9zm0 4h6v2H7v-2z" fill="currentColor"/></svg>',
      'collection-1': '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" class="placeholder-svg"><path d="M18 4H2c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM2 14V6h16v8H2z" fill="currentColor"/></svg>',
      'article-1': '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" class="placeholder-svg"><path d="M17 3H3c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 12H3V5h14v10zM5 7h10v2H5V7zm0 4h10v2H5v-2z" fill="currentColor"/></svg>'
    };
    return placeholders[type] || placeholders['product-1'];
  }

  setupVoiceSearch() {
    if (!this.isVoiceSearchSupported) {
      const voiceBtns = document.querySelectorAll('.voice-search-btn');
      voiceBtns.forEach(btn => {
        btn.style.display = 'none';
      });
      return;
    }

    const voiceBtns = document.querySelectorAll('.voice-search-btn');

    if (voiceBtns.length === 0) return;

    voiceBtns.forEach(voiceBtn => {
      voiceBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.startVoiceSearch();
      });
    });
  }

  startVoiceSearch() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Voice search is not supported in your browser. Please try Chrome, Edge, or Safari.');
      return;
    }

    const recognition = new SpeechRecognition();
    const voiceBtns = document.querySelectorAll('.voice-search-btn');
    const searchInputs = document.querySelectorAll('.search-input');

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    voiceBtns.forEach(btn => btn.classList.add('listening'));

    recognition.onstart = () => {

    };

    recognition.onresult = (event) => {
      let transcript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        } else {
          transcript += event.results[i][0].transcript;
        }
      }

      searchInputs.forEach(input => {
        input.value = transcript;

        const inputEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(inputEvent);
      });
    };

    recognition.onerror = (event) => {
      voiceBtns.forEach(btn => btn.classList.remove('listening'));

      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        alert('Microphone access denied. Please enable microphone permissions in your browser settings.');
      } else if (event.error === 'no-speech') {
        alert('No speech detected. Please try again.');
      } else if (event.error === 'network') {
        alert('Network error. Please check your internet connection.');
      }
    };

    recognition.onend = () => {
      voiceBtns.forEach(btn => btn.classList.remove('listening'));
    };

    try {
      recognition.start();
    } catch (error) {
      voiceBtns.forEach(btn => btn.classList.remove('listening'));
      alert('Failed to start voice recognition. Please try again.');
    }
  }

  setupDrawerMenu() {
    const drawerToggle = document.querySelector('.drawer-toggle');
    const drawerMenu = document.getElementById('drawer-menu');
    const drawerClose = document.querySelector('.drawer-close');
    const drawerOverlay = document.querySelector('.drawer-overlay');

    if (!drawerToggle || !drawerMenu) return;

    drawerToggle.addEventListener('click', () => {
      this.openDrawer();
    });

    drawerClose?.addEventListener('click', () => {
      this.closeDrawer();
    });

    drawerOverlay?.addEventListener('click', () => {
      this.closeDrawer();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawerMenu.classList.contains('active')) {
        this.closeDrawer();
      }
    });

    this.setupDrawerMenuItems();
  }

  setupDrawerMenuItems() {
    const drawerMenuItems = document.querySelectorAll('.drawer-menu-item.has-children');
    const drawerSubmenuItems = document.querySelectorAll('.drawer-submenu-item.has-children');

    drawerMenuItems.forEach(item => {
      const link = item.querySelector('.drawer-menu-link');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleDrawerMenuItem(item);
      });
    });

    drawerSubmenuItems.forEach(item => {
      const link = item.querySelector('.drawer-submenu-link');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleDrawerMenuItem(item);
      });
    });
  }

  toggleDrawerMenuItem(item) {
    const isExpanded = item.classList.contains('expanded');

    if (isExpanded) {
      item.classList.remove('expanded');
    } else {
      const siblings = item.parentElement.querySelectorAll('.expanded');
      siblings.forEach(sibling => {
        if (sibling !== item) {
          sibling.classList.remove('expanded');
        }
      });

      item.classList.add('expanded');
    }
  }

  openDrawer() {
    const drawerMenu = document.getElementById('drawer-menu');
    const drawerOverlay = document.querySelector('.drawer-overlay');

    this.lastFocusedElement = document.activeElement;
    drawerMenu.classList.add('active');
    drawerOverlay?.classList.add('active');
    document.body.classList.add('no-scroll');

    setTimeout(() => {
      const closeBtn = drawerMenu.querySelector('.drawer-close');
      closeBtn?.focus();
      this.trapFocus(drawerMenu);
    }, 300);
  }

  closeDrawer() {
    const drawerMenu = document.getElementById('drawer-menu');
    const drawerOverlay = document.querySelector('.drawer-overlay');

    drawerMenu.classList.remove('active');
    drawerOverlay?.classList.remove('active');
    document.body.classList.remove('no-scroll');

    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }
  }

  setupMobileMenu() {
    const mobileMenuItems = document.querySelectorAll('.mobile-menu-wrapper .menu-item');

    mobileMenuItems.forEach(item => {
      const link = item.querySelector('.menu-link');
      const hasSubmenu = item.classList.contains('has-mega-menu') ||
        item.classList.contains('has-dropdown');

      if (hasSubmenu) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleMobileMenuItem(item);
        });
      }
    });
  }

  toggleMobileMenuItem(item) {
    const isExpanded = item.getAttribute('aria-expanded') === 'true';
    item.setAttribute('aria-expanded', (!isExpanded).toString());

    const link = item.querySelector('.menu-link');
    link.setAttribute('aria-expanded', (!isExpanded).toString());
  }

  setupCartUpdates() {
    document.addEventListener('cart:updated', (event) => {
      this.updateCartCount(event.detail.item_count);
    });

    this.fetchCartCount();
  }

  async fetchCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateCartCount(cart.item_count);
    } catch (error) {
    }
  }

  updateCartCount(count) {
    const cartIcons = document.querySelectorAll('[data-cart-icon]');
    const cartCounts = document.querySelectorAll('[data-cart-count]');

    cartIcons.forEach(icon => {
      icon.setAttribute('aria-label', `Cart (${count} items)`);
    });

    cartCounts.forEach(countEl => {
      if (count > 0) {
        countEl.textContent = count;
        countEl.style.display = 'flex';
      }
    });
  }



  setupStickyHeader() {
    if (!this.stickyHeader || this.stickyHeader.dataset.sticky !== 'true') {
      return;
    }

    const wrapper = this.stickyHeader.parentElement;
    const announcement = document.querySelector('.announcement-bar-section');
    const isTransparent = wrapper?.classList.contains('header-transparent');
    let lastScrollY = window.scrollY;
    let rafId = null;

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const announcementHeight = announcement ? announcement.offsetHeight : 0;

        if (isTransparent && currentScrollY <= announcementHeight) {
          this.stickyHeader.style.top = (announcementHeight - currentScrollY) + 'px';
        } else {
          this.stickyHeader.style.top = '0px';
        }

        if (currentScrollY > Math.max(50, announcementHeight)) {
          if (!this.stickyHeader.classList.contains('scrolled')) {
            const rect = this.stickyHeader.getBoundingClientRect();
            if (wrapper && !isTransparent) {
              wrapper.style.height = rect.height + 'px';
            }
            this.stickyHeader.classList.add('scrolled');
          }
        } else {
          if (this.stickyHeader.classList.contains('scrolled')) {
            this.stickyHeader.classList.remove('scrolled');
            if (wrapper && !isTransparent) {
              wrapper.style.height = 'auto';
            }
          }
        }

        lastScrollY = currentScrollY;
        this.updateMenuTop();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    window.addEventListener('resize', () => {
      requestAnimationFrame(() => {
        handleScroll();
        this.updateMenuTop();
      });
    }, { passive: true });

    handleScroll();
  }
}

let headerManagerInstance = null;

document.addEventListener('DOMContentLoaded', () => {
  headerManagerInstance = new HeaderManager();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.site-header')) {
    headerManagerInstance = new HeaderManager();
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target.querySelector('.site-header')) {
    headerManagerInstance = null;
  }
});

window.ThemeHeader = {
  openSearch() {
    if (headerManagerInstance) {
      headerManagerInstance.openSearch();
    }
  },
  closeSearch() {
    if (headerManagerInstance) {
      headerManagerInstance.closeSearch();
    }
  },
  updateCartCount(count) {
    if (headerManagerInstance) {
      headerManagerInstance.updateCartCount(count);
    }
  }
};